import type { CompactPlan, PickedSeat, Seatmap } from "./types";

export type Cell = "empty" | "free" | "sold" | "blocked" | "pmr" | "pmr-sold" | "picked";

export type PlanRow = {
  name: string | null;
  cells: Cell[];
  gap?: boolean;
  /** classes additionnelles par cellule : atténuation et bords de la zone retenue */
  marks?: string[];
};

/** Vue commune aux deux formats de plan, écran en bas : le fond de salle est en haut. */
export type PlanView = { columns: number; rows: PlanRow[] };

const COMPACT: Record<string, Cell> = {
  ".": "empty",
  o: "free",
  x: "sold",
  n: "blocked",
  h: "pmr",
  H: "pmr-sold",
};

const isGap = (cells: Cell[]) => cells.every((cell) => cell === "empty");

export function fromCompact(plan: CompactPlan): PlanView {
  return {
    columns: plan.columns,
    rows: plan.rows
      .map((row) => {
        const cells = [...row.cells].map((char) => COMPACT[char] ?? "empty");
        return { name: row.name, cells, gap: isGap(cells) };
      })
      .reverse(),
  };
}

export function fromSeatmap(seatmap: Seatmap, picked: PickedSeat[] = []): PlanView {
  const wanted = new Set(picked.map((seat) => `${seat.row}${seat.name}`));

  const rows = seatmap.rows.map((row) => {
    const cells: Cell[] = new Array(seatmap.columns).fill("empty");

    for (const seat of row.seats) {
      const isPicked = wanted.has(`${row.name}${seat.name}`);
      const isPmr = seat.type === "DIS" && seat.state !== "blocked";
      cells[seat.index] = isPicked
        ? "picked"
        : isPmr
          ? seat.state === "sold"
            ? "pmr-sold"
            : "pmr"
          : seat.state;
    }

    return { name: row.name, cells, gap: isGap(cells) };
  });

  return { columns: seatmap.columns, rows: rows.reverse() };
}

export const CELL_LABELS: Record<Cell, string> = {
  empty: "",
  free: "libre",
  sold: "occupée",
  blocked: "indisponible",
  pmr: "PMR libre",
  "pmr-sold": "PMR occupée",
  picked: "proposée",
};
